import { useEffect, useState } from "react";
import { initActivity } from "../discord/activity.js";
import { setAccessToken, apiFetch, ApiError } from "../api/client.js";
import { traceAuthStep, AuthTimeoutError, AUTH_TIMEOUT_HINT, SERVER_AUTH_STEP_TIMEOUT_MS } from "../discord/authTrace.js";
import { isEmbeddedInDiscord } from "../runtime.js";
import {
  loadToken,
  saveToken,
  clearBrowserAuth,
  isTokenExpired,
  completeSignInFromUrl,
  canAttemptSilent,
  clearSilentAttempt,
  beginSignIn,
  exchangeBrowserCode,
} from "../discord/browserAuth.js";

export type AuthState =
  | { status: "loading" }
  | { status: "authorized"; userId: string; username: string }
  | { status: "denied"; reason: string }
  | { status: "error"; message: string }
  /** Browser mode, not signed in. `canSignIn` false ⇒ this app has no browser mode — open it from Discord. */
  | { status: "signed-out"; canSignIn: boolean; message?: string };

export interface UseAuthOptions {
  /** This app's browser-mode URL (VITE_BROWSER_URL). Omit/empty ⇒ Discord-only app. */
  browserUrl?: string;
}

const DENIED: AuthState = { status: "denied", reason: "you don't hold a role that opens this app" };

const BACKEND_BASE = (import.meta.env.VITE_BACKEND_BASE_URL as string) ?? "";

/**
 * Format any thrown value into a user-readable string. The Discord embedded-app
 * SDK rejects with plain `{code, message}` objects rather than Error instances,
 * so a naive `String(err)` renders as `[object Object]` — which hides the
 * actual failure (e.g. RPC_CONNECTION_TIMEOUT, invalid client ID, blocked
 * URL mapping). Surface the structured info instead.
 */
function formatAuthError(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (err !== null && typeof err === "object") {
    const obj = err as { code?: unknown; message?: unknown };
    const code = obj.code !== undefined ? `[${String(obj.code)}] ` : "";
    const message = typeof obj.message === "string" ? obj.message : "";
    if (code || message) return `${code}${message}`.trim();
    try {
      return JSON.stringify(err);
    } catch {
      return String(err);
    }
  }
  return String(err);
}

/** Reads the silent-attempt guard BEFORE clearBrowserAuth resets it. */
function retrySilentlyOrSignOut(appPath: string, clientId: string, browserUrl: string): AuthState {
  const allowed = canAttemptSilent(appPath);
  clearBrowserAuth(appPath);
  if (!allowed) return { status: "signed-out", canSignIn: true };
  beginSignIn({ appPath, clientId, browserUrl, silent: true });
  return { status: "loading" };
}

async function runBrowserAuth(
  appPath: string,
  clientId: string,
  browserUrl: string,
): Promise<AuthState> {
  const callback = completeSignInFromUrl(appPath);
  if (callback.kind === "error") return { status: "signed-out", canSignIn: true };
  if (callback.kind === "code") {
    try {
      saveToken(appPath, await exchangeBrowserCode(appPath, callback.code, browserUrl));
    } catch (err) {
      return {
        status: "signed-out",
        canSignIn: true,
        message: err instanceof Error ? err.message : "Sign-in failed — please try again.",
      };
    }
  }

  const token = loadToken(appPath);
  if (!token) return { status: "signed-out", canSignIn: true };

  if (isTokenExpired(token)) return retrySilentlyOrSignOut(appPath, clientId, browserUrl);

  setAccessToken(token.accessToken);
  (window as unknown as { __token?: string }).__token = token.accessToken;
  try {
    const v = await apiFetch<{ userId?: string; username?: string }>(`${appPath}/auth/verify`, {
      method: "POST",
    });
    clearSilentAttempt(appPath);
    return { status: "authorized", userId: v.userId ?? "", username: v.username ?? "" };
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      return retrySilentlyOrSignOut(appPath, clientId, browserUrl);
    }
    throw err;
  }
}

export function useAuth(
  appPath: string,
  clientId: string,
  options: UseAuthOptions = {},
): AuthState {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  useEffect(() => {
    (async () => {
      try {
        if (!isEmbeddedInDiscord()) {
          const browserUrl = options.browserUrl ?? "";
          setState(
            browserUrl
              ? await runBrowserAuth(appPath, clientId, browserUrl)
              : { status: "signed-out", canSignIn: false },
          );
          return;
        }
        const auth = await initActivity(appPath, clientId, BACKEND_BASE);
        setAccessToken(auth.accessToken);
        (window as unknown as { __token?: string }).__token = auth.accessToken;
        await traceAuthStep(
          "verify",
          () => apiFetch(`${appPath}/auth/verify`, { method: "POST" }),
          SERVER_AUTH_STEP_TIMEOUT_MS,
        );
        setState({ status: "authorized", userId: auth.userId, username: auth.username });
      } catch (err) {
        console.error("[useAuth] auth failed:", err);
        if (err instanceof ApiError && err.status === 403) {
          setState(DENIED);
        } else if (err instanceof AuthTimeoutError) {
          // A step hung (commonly a browser ad blocker dropping a background
          // request). Show an actionable hint instead of the raw timeout text.
          setState({ status: "error", message: AUTH_TIMEOUT_HINT });
        } else {
          setState({ status: "error", message: formatAuthError(err) });
        }
      }
    })();
  }, []);

  return state;
}
